import { InformationCircleIcon } from "icons/outline";
import { Card, cn, Tooltip } from "ui";
import { ChartLoadingSkeleton } from "./ChartLoadingSkeleton";
import { FailedToFetchData } from "../Skeletons";
import { forwardRef } from "react";
import type { ErrorResponse } from "../../../../hooks/useMoneyHashQuery";

export const ChartCard = forwardRef<
  HTMLDivElement,
  {
    title: string;
    subTitle?: string;
    description?: string;
    children: React.ReactNode;
    className?: string;
    contentClassName?: string;
    headerAction?: React.ReactNode;
    queryStatus?: "loading" | "error" | "success";
    error?: ErrorResponse | null;
    loadingSkeleton?: React.ReactNode;
    retry?: () => void;
  }
>(function ChartCard(
  {
    title,
    subTitle,
    description,
    children,
    className,
    contentClassName,
    headerAction,
    queryStatus = "success",
    error,
    loadingSkeleton,
    retry,
  },
  ref
) {
  const showLoading = queryStatus === "loading";
  const showError = queryStatus === "error";
  const showContent = queryStatus === "success";

  return (
    <Card disableHover className={cn("flex flex-col p-0", className)} ref={ref}>
      <div className="flex flex-wrap items-center justify-between px-6 py-4">
        <div>
          {subTitle && (
            <p className="text-sm font-medium text-foundations-subtle">
              {subTitle}
            </p>
          )}

          <div className="flex items-center gap-1">
            <h3 className="text-lg font-bold text-foundations-boldest">
              {title}
            </h3>

            {description && (
              <Tooltip>
                <Tooltip.Trigger delay={150}>
                  <InformationCircleIcon className="size-4 text-foundations-subtle" />
                </Tooltip.Trigger>
                <Tooltip.Popup variant="light">{description}</Tooltip.Popup>
              </Tooltip>
            )}
          </div>
        </div>

        {headerAction && <div className="ml-auto pl-4">{headerAction}</div>}
      </div>

      <div
        className={cn(
          "flex h-80 flex-col px-6 pb-6",
          contentClassName,
          !showContent && "px-6"
        )}
      >
        {showLoading && (loadingSkeleton || <ChartLoadingSkeleton />)}
        {showError && <FailedToFetchData error={error} retry={retry} />}
        {showContent && children}
      </div>
    </Card>
  );
});
